import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { shop, type CheckoutItem, type PaymentMethod } from "@/api/shop";
import { ApiError } from "@/api/http";
import { useAuth } from "@/auth/AuthContext";
import { Price } from "@/components/Price";

interface Props {
  open: boolean;
  onClose: () => void;
  items: CheckoutItem[];
  title: string;
  totalCents?: number;
  currency?: string;
  /** Called after a successful dev activation (e.g. to clear the cart). */
  onActivated?: () => void;
}

const METHODS: { id: PaymentMethod; label: string; icon: string }[] = [
  { id: "card", label: "Credit / debit card", icon: "💳" },
  { id: "apple_pay", label: "Apple Pay", icon: "" },
  { id: "google_pay", label: "Google Pay", icon: "" },
  { id: "paypal", label: "PayPal", icon: "" },
];

export function PaymentDialog({ open, onClose, items, title, totalCents, currency, onActivated }: Props) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: config } = useQuery({
    queryKey: ["shop", "config"],
    queryFn: shop.config,
    enabled: open,
  });

  if (!open) return null;

  const methods = config?.methods ?? [];

  async function pay(method: PaymentMethod) {
    setBusy(method);
    setError(null);
    try {
      const res = await shop.checkout(items, method);
      window.location.href = res.checkout_url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not start payment.");
      setBusy(null);
    }
  }

  async function activateFree() {
    setBusy("dev");
    setError(null);
    try {
      await shop.devActivate(items);
      await qc.invalidateQueries({ queryKey: ["shop", "entitlements"] });
      await qc.invalidateQueries({ queryKey: ["shop", "orders"] });
      onActivated?.();
      onClose();
      navigate("/checkout/success?dev=1");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Activation failed.");
      setBusy(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-line bg-surface p-6 shadow-card-hover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-extrabold tracking-tight text-ink">Choose how to pay</h2>
            <p className="mt-0.5 text-sm text-ink-soft">{title}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-ink-mute hover:bg-surface-alt hover:text-ink">
            ✕
          </button>
        </div>

        {totalCents != null && (
          <div className="mt-4 flex items-center justify-between rounded-xl bg-surface-alt px-4 py-3">
            <span className="text-sm text-ink-soft">Total</span>
            <span className="text-lg font-bold text-ink">
              <Price cents={totalCents} currency={currency} />
            </span>
          </div>
        )}

        {!isAuthenticated ? (
          <div className="mt-5">
            <p className="text-sm text-ink-soft">Please sign in to complete your purchase.</p>
            <button
              onClick={() => navigate("/login", { state: { returnTo: location.pathname } })}
              className="mt-3 w-full rounded-lg bg-brand px-4 py-2.5 font-semibold text-white hover:bg-brand-strong"
            >
              Sign in to continue
            </button>
          </div>
        ) : (
          <>
            <div className="mt-5 space-y-2">
              {METHODS.map((m) => {
                const enabled = methods.includes(m.id);
                return (
                  <button
                    key={m.id}
                    disabled={!enabled || busy != null}
                    onClick={() => pay(m.id)}
                    className="flex w-full items-center justify-between rounded-xl border border-line bg-surface px-4 py-3 text-left transition-colors enabled:hover:border-brand enabled:hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-55"
                  >
                    <span className="flex items-center gap-3 font-medium text-ink">
                      {m.icon && <span className="text-lg">{m.icon}</span>}
                      {m.label}
                    </span>
                    {enabled ? (
                      <span className="text-sm font-semibold text-brand-strong">
                        {busy === m.id ? "…" : "Pay →"}
                      </span>
                    ) : (
                      <span className="rounded-full bg-surface-alt px-2 py-0.5 text-[0.68rem] font-semibold uppercase text-ink-mute">
                        Soon
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {methods.length === 0 && (
              <p className="mt-3 text-xs text-ink-mute">
                Live payments are being set up. Use testing activation below for now.
              </p>
            )}

            {config?.allow_dev_activation && (
              <div className="mt-5 border-t border-dashed border-line pt-4">
                <button
                  disabled={busy != null}
                  onClick={activateFree}
                  className="w-full rounded-lg border-2 border-dashed border-warn/50 bg-warn/5 px-4 py-2.5 font-semibold text-warn transition-colors hover:bg-warn/10 disabled:opacity-60"
                >
                  {busy === "dev" ? "Activating…" : "⚡ Activate without payment (testing)"}
                </button>
                <p className="mt-1.5 text-center text-[0.7rem] text-ink-mute">
                  Bypasses payment and unlocks the add-on immediately — testing only.
                </p>
              </div>
            )}
          </>
        )}

        {error && <p className="mt-4 text-sm text-down">{error}</p>}
      </div>
    </div>
  );
}
